import React from "react";
import { Quote, TrendingDown, Globe2 } from "lucide-react";
import { stats, labImage } from "../data/mock";

export default function WhyActNow() {
  return (
    <section id="why" className="relative py-24 bg-[#FAF9F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left */}
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C4433D]/10 text-[#C4433D] mb-5">
              <Globe2 className="w-4 h-4" />
              <span className="text-xs font-bold tracking-widest uppercase">
                Why Act Now
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-[#0f2a20] mb-6">
              The case for <span className="text-[#1F5943]">sustainable</span>
              <br />
              science.
            </h2>
            <p className="text-[#3a5148] text-lg leading-relaxed mb-5">
              Biotech is an engine of discovery — but it carries a significant
              environmental footprint. Labs consume 5–10× more energy per square
              foot than standard office buildings, and single-use plastics are
              endemic across the sector.
            </p>
            <p className="text-[#3a5148] text-lg leading-relaxed mb-8">
              Yet biotech sits at a unique intersection: the science we do can
              solve planetary crises — and our labs can model the solutions. The
              Sustainability Leader Awards recognise those leading the charge.
            </p>

            <div className="relative p-6 rounded-2xl bg-white border border-[#1F5943]/10 shadow-sm">
              <Quote className="absolute -top-3 -left-3 w-8 h-8 p-1.5 rounded-full bg-[#C4433D] text-white" />
              <p className="italic text-[#0f2a20] text-lg leading-relaxed">
                “There is a rapidly closing window of opportunity to secure a
                liveable future for all.”
              </p>
              <div className="mt-3 text-sm font-semibold text-[#7a8b83]">
                — IPCC 6th Assessment Report, 2023
              </div>
            </div>
          </div>

          {/* Right: Stats + Image */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden mb-6 shadow-xl">
              <img src={labImage} alt="Sustainable lab" className="w-full h-72 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f2a20]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <div className="text-white/80 text-xs uppercase tracking-widest font-semibold mb-1">
                    UN Sustainable Development Goals
                  </div>
                  <div className="text-white font-display text-2xl">17 Goals, One Planet</div>
                </div>
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg">
                  <TrendingDown className="w-6 h-6 text-[#C4433D]" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div
                  key={s.value}
                  className="p-6 rounded-2xl bg-white border border-[#1F5943]/10 hover:border-[#1F5943]/30 hover:shadow-lg transition-all group"
                >
                  <div className="font-display text-4xl text-[#1F5943] mb-2 group-hover:scale-105 transition-transform origin-left">
                    {s.value}
                  </div>
                  <div className="font-semibold text-[#0f2a20] text-sm">{s.label}</div>
                  <div className="text-[#7a8b83] text-xs mt-1">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
